import React, { useState, useEffect } from 'react';
import {
    Table,
    Tag,
    Button,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Row,
    Col,
    Statistic,
    Space,
    Popconfirm,
    Tooltip,
    Badge,
    Typography
} from 'antd';
import {
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AdminAffiliateApplications = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [stats, setStats] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [filters, setFilters] = useState({
        status: 'all',
        search: ''
    });

    const [form] = Form.useForm();

    // Helper to get JWT token from local storage
    const getAuthHeaders = () => {
        const token = localStorage.getItem('token'); // Replace 'jwtToken' with your actual key
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // Fetch applications
    const fetchApplications = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pageSize,
                status: filters.status,
                search: filters.search
            };

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/affiliate-applications`,
                {
                    params,
                    headers: getAuthHeaders() // Add authorization header
                }
            );

            if (response.data.success) {
                setApplications(response.data.data);
                setPagination({
                    current: page,
                    pageSize,
                    total: response.data.pagination.total
                });
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
            message.error('Failed to fetch applications');
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStats = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/affiliate-applications/stats/overview`,
                {
                    headers: getAuthHeaders() // Add authorization header
                }
            );
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    // Update application status
    const updateApplication = async (values) => {
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/affiliate-applications/${selectedApplication._id}`,
                values,
                {
                    headers: getAuthHeaders() // Add authorization header
                }
            );

            if (response.data.success) {
                message.success('Application updated successfully');
                setEditModalVisible(false);
                fetchApplications(pagination.current, pagination.pageSize);
                fetchStats();
                form.resetFields();
            }
        } catch (error) {
            console.error('Error updating application:', error);
            message.error('Failed to update application');
        }
    };

    // Delete application
    const deleteApplication = async (id) => {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/affiliate-applications/${id}`,
                {
                    headers: getAuthHeaders() // Add authorization header
                }
            );

            if (response.data.success) {
                message.success('Application deleted successfully');
                fetchApplications(pagination.current, pagination.pageSize);
                fetchStats();
            }
        } catch (error) {
            console.error('Error deleting application:', error);
            message.error('Failed to delete application');
        }
    };

    // Handle table pagination
    const handleTableChange = (pagination) => {
        fetchApplications(pagination.current, pagination.pageSize);
    };

    // Handle filters
    const handleFilterChange = () => {
        fetchApplications(1, pagination.pageSize);
    };

    useEffect(() => {
        fetchApplications();
        fetchStats();
    }, [filters]);

    // Table columns
    const columns = [
        {
            title: 'Name',
            dataIndex: 'fullName',
            key: 'fullName',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    pending: 'orange',
                    approved: 'green',
                    rejected: 'red'
                };
                return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Submitted',
            dataIndex: 'submittedAt',
            key: 'submittedAt',
            render: (date) => new Date(date).toLocaleDateString()
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => {
                                setSelectedApplication(record);
                                setModalVisible(true);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Edit Status">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                                setSelectedApplication(record);
                                form.setFieldsValue({
                                    status: record.status,
                                    reviewNotes: record.reviewNotes
                                });
                                setEditModalVisible(true);
                            }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Are you sure you want to delete this application?"
                        onConfirm={() => deleteApplication(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete">
                            <Button danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Affiliate Applications Management</Title>

            {/* Statistics Cards */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Applications"
                            value={stats.total || 0}
                            prefix={<UserOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Pending"
                            value={stats.pending || 0}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Approved"
                            value={stats.approved || 0}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Rejected"
                            value={stats.rejected || 0}
                            prefix={<CloseCircleOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card style={{ marginBottom: '16px' }}>
                <Row gutter={16} align="middle">
                    <Col span={6}>
                        <Select
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Status</Option>
                            <Option value="pending">Pending</Option>
                            <Option value="approved">Approved</Option>
                            <Option value="rejected">Rejected</Option>
                        </Select>
                    </Col>
                    <Col span={8}>
                        <Input.Search
                            placeholder="Search by name, email, or mobile"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            onSearch={handleFilterChange}
                            allowClear
                        />
                    </Col>
                    <Col>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                fetchApplications(pagination.current, pagination.pageSize);
                                fetchStats();
                            }}
                        >
                            Refresh
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Applications Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={applications}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} applications`
                    }}
                    onChange={handleTableChange}
                />
            </Card>

            {/* View Details Modal */}
            <Modal
                title="Application Details"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedApplication && (
                    <div>
                        <Row gutter={16}>
                            <Col span={12}>
                                <p><strong>Name:</strong> {selectedApplication.fullName}</p>
                                <p><strong>Email:</strong> {selectedApplication.email}</p>
                                <p><strong>Mobile:</strong> {selectedApplication.mobile}</p>
                            </Col>
                            <Col span={12}>
                                <p><strong>Status:</strong>
                                    <Tag color={
                                        selectedApplication.status === 'pending' ? 'orange' :
                                        selectedApplication.status === 'approved' ? 'green' : 'red'
                                    }>
                                        {selectedApplication.status.toUpperCase()}
                                    </Tag>
                                </p>
                                <p><strong>Submitted:</strong> {new Date(selectedApplication.submittedAt).toLocaleString()}</p>
                                {selectedApplication.reviewedAt && (
                                    <p><strong>Reviewed:</strong> {new Date(selectedApplication.reviewedAt).toLocaleString()}</p>
                                )}
                            </Col>
                        </Row>

                        {selectedApplication.websiteOrSocial && (
                            <p>
                                <strong>Website/Social:</strong>
                                <a href={selectedApplication.websiteOrSocial} target="_blank" rel="noopener noreferrer">
                                    {selectedApplication.websiteOrSocial}
                                </a>
                            </p>
                        )}

                        {selectedApplication.howYouHeard && (
                            <div>
                                <strong>How they heard about us:</strong>
                                <p style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                                    {selectedApplication.howYouHeard}
                                </p>
                            </div>
                        )}

                        {selectedApplication.message && (
                            <div>
                                <strong>Additional Message:</strong>
                                <p style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                                    {selectedApplication.message}
                                </p>
                            </div>
                        )}

                        {selectedApplication.reviewNotes && (
                            <div>
                                <strong>Review Notes:</strong>
                                <p style={{ background: '#fff2e8', padding: '8px', borderRadius: '4px' }}>
                                    {selectedApplication.reviewNotes}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Edit Status Modal */}
            <Modal
                title="Update Application Status"
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={updateApplication}
                >
                    <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select>
                            <Option value="pending">Pending</Option>
                            <Option value="approved">Approved</Option>
                            <Option value="rejected">Rejected</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="reviewNotes"
                        label="Review Notes"
                    >
                        <TextArea
                            rows={4}
                            placeholder="Add any notes about your decision..."
                        />
                    </Form.Item>

                    <Form.Item style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => {
                                setEditModalVisible(false);
                                form.resetFields();
                            }}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Update Application
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AdminAffiliateApplications;